import { GET_DOCUMENTS_ENUM_OPTIONS } from "queries/neoFact/documents";
import { clientCentralApiStrawberry, clientNeoBanks, clientNeoFact } from "@neonumy/api-clients";
import { LIST_ADDRESSES_COMPANY } from "queries/centralApi/addresses";
import { LIST_UNIT_MEASURES } from "queries/neoFact/neofact";
import useSettingsStore from "views/hooks/useSettingsStore";
import { LIST_COMPANY_BANK_ACCOUNTS } from "queries/banks";
import { useLazyQuery, useMutation } from "@apollo/client";
import { onErrorGraphql } from "utils/onErrorGraphql";
import useCoreStore from "views/hooks/useCoreStore";
import { FormInstance } from "antd";
import { useEffect, useMemo } from "react";
import {
  GET_COMPANY_SETTINGS,
  UPDATE_COMPANY_ADDRESS,
  CREATE_COMPANY_ADDRESS,
  UPDATE_COMPANY_SETTINGS,
  LIST_DEFAULT_PAYMENT_METHODS,
  UPDATE_COMPANY,
} from "queries/centralApi/companies";
import {
  GET_COMPANY_PAYMENT_DEADLINE,
  LIST_COMPANY_PAYMENT_DEADLINES,
  CREATE_COMPANY_PAYMENT_DEADLINE,
  UPDATE_COMPANY_PAYMENT_DEADLINE,
  LIST_EXPORT_FORMATS_ENUM_OPTIONS,
} from "queries/neoFact/settings";
import { useTranslation } from "react-i18next";

export default function useGeneralApi(id?: ID, form?: FormInstance<any>) {
  const { t } = useTranslation();

  const setDefaultUnitMeasureId = useSettingsStore(state => state.setDefaultUnitMeasureId);
  const setCompanyMainAddress = useSettingsStore(state => state.setCompanyMainAddress);
  const setActiveUnitMeasures = useSettingsStore(state => state.setActiveUnitMeasures);
  const setPaymentOptions = useSettingsStore(state => state.setPaymentOptions);
  const setCompanySettings = useCoreStore(state => state.setCompanySettings);
  const setUnitMeasures = useSettingsStore(state => state.setUnitMeasures);
  const setCurrency = useCoreStore(state => state.setCurrency);

  const [
    listCompaniesBankAccountsPaginated,
    {
      refetch: listCompanyBankPaginatedRefetch,
      loading: listCompanyBankPaginatedLoading,
      data: listCompaniesBankAccountsPaginatedData,
      error: listCompaniesBankAccountsPaginatedError,
    },
  ] = useLazyQuery<
    ListCompaniesBankAccountsPaginatedOutput,
    ListCompaniesBankAccountsPaginatedInput
  >(LIST_COMPANY_BANK_ACCOUNTS, {
    client: clientNeoBanks,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (listCompaniesBankAccountsPaginatedError)
      onErrorGraphql(listCompaniesBankAccountsPaginatedError);
  }, [listCompaniesBankAccountsPaginatedError]);

  const companiesBankAccountsData: ListCompaniesBankAccountsPaginatedData[] =
    listCompaniesBankAccountsPaginatedData?.listCompaniesBankAccountsPaginated?.results?.map(
      (item: any) => ({ ...item, key: item.id }),
    ) ?? [];

  const [
    getCompanySetting,
    {
      refetch: refetchGetCompanySetting,
      loading: companySettingLoading,
      data: getCompanySettingData,
      error: getCompanySettingError,
    },
  ] = useLazyQuery<GetCompanySettingResponse, GetVarTypeID>(GET_COMPANY_SETTINGS, {
    client: clientNeoFact,
  });

  useEffect(() => {
    if (getCompanySettingError) onErrorGraphql(getCompanySettingError);
  }, [getCompanySettingError]);

  useEffect(() => {
    const getCompanySetting = getCompanySettingData?.getCompanySetting;
    if (!getCompanySetting) return;

    const mainCurrency = getCompanySetting?.currencySettings?.find(item => item?.isMainCurrency);
    if (mainCurrency) {
      setCurrency(mainCurrency);
    }
    setCompanySettings(getCompanySetting as any);

    const filteredMethods =
      getCompanySetting?.defaultPaymentMethods?.filter(item => item.code !== "RE") ?? [];
    const paymentOptions = filteredMethods.map(item => ({
      label: item.name,
      value: String(item.id),
    }));
    setPaymentOptions(paymentOptions);
  }, [getCompanySettingData]);

  const [updateCompany, { loading: updateCompanyInvoiceLoading }] = useMutation<
    UpdateCompanyOutput,
    UpdateCompanyVars
  >(UPDATE_COMPANY, {
    client: clientCentralApiStrawberry,
  });

  const [updateCompanyAddress, { loading: updateCompanyAddressLoading }] = useMutation<
    UpdateCompanyAddressMutationResponse,
    UpdateCompanyAddressVars
  >(UPDATE_COMPANY_ADDRESS, {
    client: clientCentralApiStrawberry,
  });

  const [createCompanyAddress, { loading: createCompanyAddressLoading }] = useMutation<
    CreateCompanyAddressMutationResponse,
    CreateCompanyAddressVars
  >(CREATE_COMPANY_ADDRESS, {
    client: clientCentralApiStrawberry,
  });

  const [updateCompanySetting, { loading: updateCompanySettingLoading }] = useMutation<
    UpdateCompanySettingMutationResponse,
    UpdateCompanySettingVars
  >(UPDATE_COMPANY_SETTINGS, {
    client: clientNeoFact,
  });

  const [
    listAddresses,
    {
      refetch: refetchAddresses,
      loading: addressesLoading,
      data: listAddressesData,
      error: listAddressesError,
    },
  ] = useLazyQuery<ListAddressesCompanyResponse, ListAddressesCompanyVars>(LIST_ADDRESSES_COMPANY, {
    client: clientCentralApiStrawberry,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (listAddressesError) onErrorGraphql(listAddressesError);
  }, [listAddressesError]);

  const mainAddress = listAddressesData?.listAddresses?.results?.[0];

  useEffect(() => {
    if (mainAddress) {
      setCompanyMainAddress(mainAddress as unknown as Parameters<typeof setCompanyMainAddress>[0]);
    }
  }, [mainAddress]);

  const [createUpdatePaymentDeadline] = useMutation<
    CreateUpdateCompanyPaymentDeadlineResponse,
    CreateUpdateCompanyPaymentDeadlineVars
  >(id ? UPDATE_COMPANY_PAYMENT_DEADLINE : CREATE_COMPANY_PAYMENT_DEADLINE, {
    client: clientNeoFact,
  });

  const [getPaymentDeadline, { data: getPaymentDeadlineData, error: getPaymentDeadlineError }] =
    useLazyQuery<GetCompanyPaymentDeadlineResponse, GetVarTypeID>(GET_COMPANY_PAYMENT_DEADLINE, {
      client: clientNeoFact,
    });

  useEffect(() => {
    if (getPaymentDeadlineError) onErrorGraphql(getPaymentDeadlineError);
  }, [getPaymentDeadlineError]);

  useEffect(() => {
    const getCompanyPaymentDeadline = getPaymentDeadlineData?.getCompanyPaymentDeadline;
    if (!getCompanyPaymentDeadline) return;

    const fieldsData: any[] = [];
    for (const key in getCompanyPaymentDeadline) {
      fieldsData.push({
        name: key,
        value: (getCompanyPaymentDeadline as Record<string, any>)[key],
      });
    }

    form?.setFields(fieldsData);
  }, [getPaymentDeadlineData, form]);

  const [listInvoiceTypes, { data: listInvoiceTypesData, error: listInvoiceTypesError }] =
    useLazyQuery<GetDocumentsEnumOptionsResponse>(GET_DOCUMENTS_ENUM_OPTIONS, {
      client: clientNeoFact,
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
    });

  useEffect(() => {
    if (listInvoiceTypesError) onErrorGraphql(listInvoiceTypesError);
  }, [listInvoiceTypesError]);

  const invoiceTypeOptions: SaleDocumentType[] = useMemo(
    () =>
      (listInvoiceTypesData?.invoiceType?.map((item: any) => ({
        label: item.label,
        value: item.value,
      })) ?? []) as unknown as SaleDocumentType[],
    [listInvoiceTypesData],
  );

  const [listExportFormats, { data: listExportFormatsData, error: listExportFormatsError }] =
    useLazyQuery<ListExportFormatsEnumOptionsResponse>(LIST_EXPORT_FORMATS_ENUM_OPTIONS, {
      client: clientNeoFact,
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
    });

  useEffect(() => {
    if (listExportFormatsError) onErrorGraphql(listExportFormatsError);
  }, [listExportFormatsError]);

  const exportFormatsOptions = useMemo(
    () =>
      listExportFormatsData?.listExportFormats?.map((item: any) => ({
        label: item.label,
        value: item.value,
      })) ?? [],
    [listExportFormatsData],
  );

  const [
    listPaymentDeadlines,
    {
      refetch: refetchListPaymentDeadlines,
      loading: loadingListPaymentDeadlines,
      data: listPaymentDeadlinesQueryData,
      error: listPaymentDeadlinesError,
    },
  ] = useLazyQuery<ListCompanyPaymentDeadlinesResponse, ListCompanyPaymentDeadlinesVars>(
    LIST_COMPANY_PAYMENT_DEADLINES,
    {
      client: clientNeoFact,
    },
  );

  useEffect(() => {
    if (listPaymentDeadlinesError) onErrorGraphql(listPaymentDeadlinesError);
  }, [listPaymentDeadlinesError]);

  const listPaymentDeadlinesData =
    listPaymentDeadlinesQueryData?.listCompanyPaymentDeadlines?.results ?? [];
  const listPaymentDeadlinesTotalCount: number | undefined =
    listPaymentDeadlinesQueryData?.listCompanyPaymentDeadlines?.totalCount;

  const [
    listDefaultPaymentMethods,
    { data: listDefaultPaymentMethodsData, error: listDefaultPaymentMethodsError },
  ] = useLazyQuery<ListPaymentMethodsResponse, ListPaymentMethodsVars>(
    LIST_DEFAULT_PAYMENT_METHODS,
    {
      client: clientCentralApiStrawberry,
    },
  );

  useEffect(() => {
    if (listDefaultPaymentMethodsError) onErrorGraphql(listDefaultPaymentMethodsError);
  }, [listDefaultPaymentMethodsError]);

  const paymentMethodsOptions = useMemo(
    () =>
      listDefaultPaymentMethodsData?.listPaymentMethods?.results
        ?.filter((item: { code: string }) => item.code !== "RE")
        .map((item: { id: ID; name: string; code: string }) => ({
          label: t(item.name),
          value: String(item.id),
          key: String(item.id),
        })) ?? [],
    [listDefaultPaymentMethodsData, t],
  );

  const [
    listUnitMeasures,
    {
      loading: listUnitMeasuresLoading,
      refetch: listUnitMeasuresRefetch,
      data: listUnitMeasuresData,
      error: listUnitMeasuresError,
    },
  ] = useLazyQuery<ListUnitMeasuresResponse, ListUnitMeasuresVars>(LIST_UNIT_MEASURES, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (listUnitMeasuresError) onErrorGraphql(listUnitMeasuresError);
  }, [listUnitMeasuresError]);

  useEffect(() => {
    const listUnitMeasures = listUnitMeasuresData?.listUnitMeasures;
    if (!listUnitMeasures?.results) return;

    const newData = listUnitMeasures.results.map(item => ({
      label: `${item.slug} - ${item?.name}`,
      value: item.id,
      key: item.id,
    }));
    setUnitMeasures(newData as any);

    const unitU = listUnitMeasures.results.find(item => item.slug === "U");
    if (unitU) {
      setDefaultUnitMeasureId(unitU.id);
    }
  }, [listUnitMeasuresData]);

  const [
    listActiveUnitMeasures,
    {
      loading: listActiveUnitMeasuresLoading,
      refetch: listActiveUnitMeasuresRefetch,
      data: listActiveUnitMeasuresData,
      error: listActiveUnitMeasuresError,
    },
  ] = useLazyQuery<ListUnitMeasuresResponse, ListUnitMeasuresVars>(LIST_UNIT_MEASURES, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (listActiveUnitMeasuresError) onErrorGraphql(listActiveUnitMeasuresError);
  }, [listActiveUnitMeasuresError]);

  useEffect(() => {
    const listUnitMeasures = listActiveUnitMeasuresData?.listUnitMeasures;
    if (!listUnitMeasures?.results) return;

    const newData = listUnitMeasures.results.map(item => ({
      label: `${item?.name}`,
      value: item.id,
      key: item.id,
    }));

    setActiveUnitMeasures(newData as any);
  }, [listActiveUnitMeasuresData]);

  return {
    mainAddress,
    listAddresses,
    listUnitMeasures,
    listInvoiceTypes,
    refetchAddresses,
    addressesLoading,
    listExportFormats,
    getCompanySetting,
    invoiceTypeOptions,
    getPaymentDeadline,
    listPaymentDeadlines,
    exportFormatsOptions,
    updateCompanySetting,
    updateCompany,
    updateCompanyAddress,
    createCompanyAddress,
    createCompanyAddressLoading,
    paymentMethodsOptions,
    companySettingLoading,
    listActiveUnitMeasures,
    listUnitMeasuresLoading,
    listUnitMeasuresRefetch,
    listPaymentDeadlinesData,
    refetchGetCompanySetting,
    listDefaultPaymentMethods,
    companiesBankAccountsData,
    refetchListPaymentDeadlines,
    loadingListPaymentDeadlines,
    updateCompanySettingLoading,
    updateCompanyAddressLoading,
    updateCompanyInvoiceLoading,
    createUpdatePaymentDeadline,
    listActiveUnitMeasuresLoading,
    listActiveUnitMeasuresRefetch,
    listPaymentDeadlinesTotalCount,
    listCompanyBankPaginatedRefetch,
    listCompanyBankPaginatedLoading,
    listCompaniesBankAccountsPaginated,
  };
}
